import { useState, useCallback } from 'react';
import { Pipeline, PipelineService, PaginatedPipelineResponse } from './pipeline.service';

interface PaginationData {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  hasMore: boolean;
  pageSize: number;
}

interface UsePipelinesReturn {
  pipelines: Pipeline[];
  loading: boolean;
  backgroundLoading: boolean;
  error: string | null;
  pagination: PaginationData;
  fetchPipelines: (page?: number, pageSize?: number, filters?: Record<string, any>) => Promise<void>;
  refreshPipelines: () => Promise<void>;
  addPipeline: (pipeline: Pipeline) => void;
  updatePipeline: (pipeline: Pipeline) => void;
  removePipeline: (pipelineId: number) => void;
  lastFetched: Date | null;
  isInitialized: boolean;
  clearBackgroundLoading: () => void;
  clearPipelinesData: () => void;
}

export const usePipelines = (): UsePipelinesReturn => {
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [loading, setLoading] = useState(false);
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetched, setLastFetched] = useState<Date | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [pagination, setPagination] = useState<PaginationData>({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    hasMore: false,
    pageSize: 10
  });

  // Cache to store fetched pages and prevent duplicate requests
  const [fetchedPages, setFetchedPages] = useState<Set<number>>(new Set());
  const [pageData, setPageData] = useState<Map<number, Pipeline[]>>(new Map());

  const clearBackgroundLoading = useCallback(() => {
    setBackgroundLoading(false);
  }, []);

  // Clear all data when tenant changes
  const clearPipelinesData = useCallback(() => {
    setPipelines([]);
    setError(null);
    setIsInitialized(false);
    setFetchedPages(new Set());
    setPageData(new Map());
    setPagination({
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,
      hasMore: false,
      pageSize: 10
    });
  }, []);

  const fetchPipelines = useCallback(async (page: number = 1, pageSize: number = 10, filters?: Record<string, any>) => {
    // For pagination, always fetch the requested page
    // The cache check is disabled for better pagination experience

    try {
      setLoading(true);
      setError(null);
      
      const response: PaginatedPipelineResponse = await PipelineService.getPipelinesByPage(page, pageSize, filters);
      
      // Update pagination data
      setPagination({
        currentPage: page,
        totalPages: response.totalPages,
        totalCount: response.totalCount,
        hasMore: response.hasMore,
        pageSize: pageSize
      });

      // Cache the page data
      setPageData(prev => new Map(prev).set(page, response.pipelines));
      setFetchedPages(prev => new Set(prev).add(page));

      // If this is the first page or we're refreshing, replace pipelines
      // Otherwise, append to existing pipelines (for infinite scroll scenarios)
      if (page === 1) {
        setPipelines(response.pipelines);
      } else {
        // For pagination, typically you'd replace with current page data
        // For infinite scroll, you'd append
        setPipelines(response.pipelines);
      }

      setLastFetched(new Date());
      setIsInitialized(true);
      
      // Set background loading after main API completes
      setBackgroundLoading(true);
    } catch (err) {
      console.error('Error fetching pipelines:', err);
      setError('Failed to fetch pipelines');
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshPipelines = useCallback(async () => {
    // Clear cache and fetch first page
    setFetchedPages(new Set());
    setPageData(new Map());
    setIsInitialized(false);
    await fetchPipelines(1, 10);
  }, [fetchPipelines]);

  const addPipeline = useCallback((pipeline: Pipeline) => {
    setPipelines(prev => [pipeline, ...prev]);
    // Update pagination count
    setPagination(prev => ({
      ...prev,
      totalCount: prev.totalCount + 1
    }));
  }, []);

  const updatePipeline = useCallback((updatedPipeline: Pipeline) => {
    setPipelines(prev => 
      prev.map(pipeline => 
        pipeline.pipeline_id === updatedPipeline.pipeline_id ? updatedPipeline : pipeline
      )
    );
  }, []);

  const removePipeline = useCallback((pipelineId: number) => {
    setPipelines(prev => prev.filter(pipeline => pipeline.pipeline_id !== pipelineId));
    // Update pagination count
    setPagination(prev => ({
      ...prev,
      totalCount: Math.max(0, prev.totalCount - 1)
    }));
  }, []);

  return {
    pipelines,
    loading,
    backgroundLoading,
    error,
    pagination,
    fetchPipelines,
    refreshPipelines,
    addPipeline,
    updatePipeline,
    removePipeline,
    lastFetched,
    isInitialized,
    clearBackgroundLoading,
    clearPipelinesData
  };
};
